import React, { useState, useEffect, useRef } from "react";
import { MdDelete, MdEdit, MdAdd, MdFileDownload, MdSort } from "react-icons/md";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { Spinner as SpinnerBoot } from "react-bootstrap";
import Select from "react-select";
import { formatDate, today } from "../../../Helper/Helper";
import { useSelector } from "react-redux";
import { SendPostRequest } from "../../../http";
import Spinner from "../../Spinner";
import * as echarts from "echarts";

// Custom CSS for active tab styling
const activeTabStyles = `
  .btn-group .btn.active-view {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  
  .btn-group .btn.active-unit {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  
  /* Reduced padding for action buttons */
  .action-buttons .btn {
    padding: 0.25rem 0.5rem;
  }
  .action-buttons  {
    display:flex;
    justify-content:center;
  }
`;

const WeightManageChart = ({ data, loading, unit, userData }) => {
  const chartRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Helper to format x-axis date labels as "12 Mar 2025" and wrap text
  const formatAxisDateLabel = (value) => {
    if (!value) return value;

    try {
      // Expecting YYYY-MM-DD or a parsable date string
      let year, month, day;

      if (typeof value === "string" && value.includes("-")) {
        const parts = value.split("-");
        // Handle YYYY-MM-DD or YYYY/MM/DD style strings
        if (parts.length >= 3) {
          year = parts[0];
          month = parts[1];
          day = parts[2].substring(0, 2); // strip any time part if present
        }
      }

      // Fallback to Date parsing if direct split failed
      if (!year || !month || !day) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          year = d.getFullYear().toString();
          month = (d.getMonth() + 1).toString().padStart(2, "0");
          day = d.getDate().toString().padStart(2, "0");
        }
      }

      if (!year || !month || !day) return value;

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthIndex = parseInt(month, 10) - 1;
      const monthName = monthNames[monthIndex] || month;

      // Single-line label: "12 Mar 2025"
      const singleLine = `${parseInt(day, 10)} ${monthName} ${year}`;

      // Wrap into two lines: "12 Mar\n2025"
      return `${parseInt(day, 10)} ${monthName}\n${year}`;
    } catch (e) {
      return value;
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (
      !data ||
      !data?.label ||
      !Array.isArray(data?.label) ||
      data?.label?.length === 0
    ) {
      return;
    }

    // Calculate min and max values for Y-axis
    const validValues = data.value.filter(
      (val) => val !== null && val !== undefined
    );
    if (validValues.length === 0) return;

    const minValue = Math.min(...validValues);
    const maxValue = Math.max(...validValues);

    // Calculate proper x-axis intervals
    const interval = Math.max(0, Math.floor(data.label.length / 10));

    // Prepare data points with colors for markers
    const seriesData = data.value.map((value, index) => ({
      value,
      itemStyle: {
        color: data.color?.[index] || "#4F81BC",
      },
    }));

    // Chart option
    const option = {
      title: {
        text: "Weight Trends",
        left: "center",
        top: "10px",
        textStyle: { fontSize: 18 },
      },
      tooltip: {
        trigger: "axis",
        // Keep tooltip within chart area to avoid going off-screen
        confine: true,
        formatter: function (params) {
          const weight = params[0];
          const value = weight.data.value || weight.data;
          const index = weight.dataIndex;
          let status = "";

          if (data.status && data.status[index]) {
            status = ` (${
              data.status[index].charAt(0).toUpperCase() +
              data.status[index].slice(1)
            })`;
          }

          return `${weight?.axisValue}<br/>${weight?.marker} ${weight?.seriesName}: ${value} ${unit}${status}`;
        },
      },
      grid: {
        top: 80,
        left: isMobile ? "3%" : "3%",
        right: isMobile ? "15%" : "8%",
        bottom: isMobile ? "2%" : "15%",
        containLabel: true,
      },
      xAxis: {
        show: !isMobile,
        type: "category",
        data: data.label,
        axisLabel: {
          // Dynamically skip some labels when there are too many to avoid overlap
          interval: (index) => {
            const len = (data.label || []).length || 0;
            if (len > 20) return index % 4 === 0; // show every 4th label
            if (len > 12) return index % 3 === 0; // show every 3rd label
            if (len > 8) return index % 2 === 0; // show every 2nd label
            return true; // show all when few
          },
          // Use custom formatter to change date format and wrap text
          formatter: (value) => formatAxisDateLabel(value),
          // Let ECharts hide overlapping labels as a fallback
          hideOverlap: true,
          // Extra space between axis and labels
          margin: 12,
          // No rotation needed when we wrap text onto two lines
          rotate: 0,
          fontSize: 10,
        },
      },
      yAxis: {
        type: "value",
        name: unit,
        nameLocation: "end",
        nameTextStyle: {
          padding: [0, 0, 0, 2],
        },
        min: minValue - 5,
        max: maxValue + 5,
        splitLine: { show: true, lineStyle: { type: "dashed" } },
        axisLabel: {
          fontSize: 12,
          align: "end",
          // Add extra gap between y-axis labels and chart area and avoid label collisions
          margin: 30,
          hideOverlap: true,
        },
      },

      //       yAxis: {
      //   type: "value",
      //   min: minValue - 5,
      //   max: maxValue + 5,name: unit,
      //       nameLocation: "end",
      //   splitLine: {
      //     show: true,
      //     lineStyle: { type: "dashed" }
      //   },
      //   axisLabel: {
      //     fontSize: 12,
      //     align: "end",

      //   }
      // },

      // ✅ VisualMap: segment-wise colors
      visualMap: {
        show: false,
        dimension: 0, // x-axis index
        pieces: data.color?.map((clr, idx) => ({
          gt: idx - 1,
          lte: idx,
          color: clr,
        })),
      },

      series: [
        {
          name: "Weight",
          type: "line",
          data: seriesData,
          smooth: true,
          symbol: "circle",
          // Smaller symbols and not all points rendered to reduce visual overlap
          symbolSize: 4,
          showAllSymbol: "auto",
          // Down-sample when there are many points to avoid clutter along the y-axis
          sampling: "lttb",
          lineStyle: { width: 2 },
          markPoint: {
            data: [
              { type: "max", name: "Max" },
              { type: "min", name: "Min" },
            ],
          },
          markLine: {
            data: [{ type: "average", name: "Avg" }],
          },
        },
      ],
      legend: {
        data: ["Weight"],
        bottom: 10,
        textStyle: { fontSize: 12 },
      },
    };

    // Add ideal weight range if userData exists
    if (userData && userData.height) {
      const heightInMeters = userData.height / 100;
      const idealMin = parseFloat(
        (18.5 * heightInMeters * heightInMeters).toFixed(1)
      );
      const idealMax = parseFloat(
        (24.9 * heightInMeters * heightInMeters).toFixed(1)
      );

      option.series[0].markArea = {
        data: [[{ yAxis: idealMin }, { yAxis: idealMax }]],
        itemStyle: { color: "none" },
      };
    }

    const chart = echarts.init(chartRef.current);
    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [data, unit, userData]);

  return loading ? (
    <div className="text-center">
      <Spinner />
    </div>
  ) : (
    <>
      <div className="text-center fw-bold pt-5">
        {!data || !data.label || data.label.length === 0
          ? "No Record Available"
          : ""}
      </div>
      <div
        ref={chartRef}
        style={{
          width: "100%",

          // height: "400px"
          height: data && data.label && data.label.length > 0 ? "400px" : "0px",
        }}
      />
    </>
  );
};

// Main Weight Management Component
function WeightManagement() {
  const patientData = useSelector((store) => store.patient.patientLogin);
  const [viewMode, setViewMode] = useState("chart"); // 'chart' or 'table'
  const [isLoading, setIsLoading] = useState(false);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [LoadingEdit, setLoadingEdit] = useState(false);
  const [LoadingDelete, setLoadingDelete] = useState(false);
  const [filterData, setFilterData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [getData, setGetData] = useState([]);
  const [graphData, setGraphData] = useState({ label: [], value: [] });
  const [editData, setEditData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [addData, setAddData] = useState({
    weight_value: "",
    record_date: "",
    notes: "",
    unit: "kg",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedTimeStamp, setSelectedTimeStamp] = useState({
    value: "4-week",
    label: "4 Weeks",
  });
  const [weightUnit, setWeightUnit] = useState("kg"); // 'kg' or 'lbs'
  const [userData, setUserData] = useState({});
  const [currentSortOption, setCurrentSortOption] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);

  const timeStamp = [
    { value: "1-week", label: "1 Week" },
    { value: "2-week", label: "2 Weeks" },
    { value: "3-week", label: "3 Weeks" },
    { value: "4-week", label: "4 Weeks" },
  ];

  // Sorting options for dropdown
  const sortOptions = [
    {
      value: { key: "record_date", direction: "desc" },
      label: "Sort by Date (Newest)",
    },
    {
      value: { key: "record_date", direction: "asc" },
      label: "Sort by Date (Oldest)",
    },
    {
      value: { key: "weight_value", direction: "desc" },
      label: "Sort by Weight (High → Low)",
    },
    {
      value: { key: "weight_value", direction: "asc" },
      label: "Sort by Weight (Low → High)",
    },
  ];
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Add custom CSS for active tabs
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = activeTabStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchUserData();
    fetchData();
    fetchGraphData();
  }, []);

  useEffect(() => {
    // Preserve filter and sort when unit changes
    fetchData(currentFilter, currentSortOption);
    fetchGraphData();
  }, [weightUnit]);

  useEffect(() => {
    fetchGraphData();
  }, [selectedTimeStamp]);

  const fetchUserData = async () => {
    try {
      // This would be replaced with actual API call to get user data
      // For demo purposes, we'll use mock data
      setUserData({
        height: 170, // in cm
        age: 35,
        gender: "male",
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const fetchData = async (filter = null, sortOption = null) => {
    setIsLoading(true);
    try {
      const response = await SendPostRequest("healthcare/weight/grid", {
        logged_id: patientData?.logged_id,
        filter: filter || {},
        unit: weightUnit, // Send the current unit preference
      });

      if (response?.status === "success") {
        // Format data based on selected unit
        const formattedData =
          response?.data?.map((item) => {
            // Use the appropriate field based on selected unit
            const weightValue =
              weightUnit === "kg"
                ? parseFloat(item.in_kg)
                : parseFloat(item.in_lbs);

            // Get status from API response
            const status = item.status || getWeightStatus(weightValue);

            return {
              ...item,
              weight_value: weightValue,
              status: status,
            };
          }) || [];

        // Apply sorting if specified
        let sortedData = formattedData;
        if (sortOption) {
          sortedData = applySorting(formattedData, sortOption);
        } else if (currentSortOption) {
          sortedData = applySorting(formattedData, currentSortOption);
        }

        setGetData(sortedData);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const applySorting = (data, sortOption) => {
    const { key, direction } = sortOption;

    return [...data].sort((a, b) => {
      if (key === "record_date") {
        const dateA = new Date(a.record_date);
        const dateB = new Date(b.record_date);
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      } else if (key === "weight_value") {
        const valA = parseFloat(a.weight_value) || 0;
        const valB = parseFloat(b.weight_value) || 0;
        return direction === "asc" ? valA - valB : valB - valA;
      }
      return 0;
    });
  };

  const fetchGraphData = async () => {
    setLoadingGraph(true);
    try {
      const response = await SendPostRequest("healthcare/weight/graph", {
        logged_id: patientData?.logged_id,
        timespan: selectedTimeStamp?.value || "4-week",
        unit: weightUnit, // Send the current unit preference
      });
      if (response?.status === "success") {
        setGraphData(response?.data || { label: [], value: [] });
      } else {
        toast.error(response?.message);
        setGraphData({ label: [], value: [] });
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch graph data");
      setGraphData({ label: [], value: [] });
    } finally {
      setLoadingGraph(false);
    }
  };

  const handleSortChange = (selectedOption) => {
    if (!selectedOption) return;

    const { key, direction } = selectedOption.value;
    setSortConfig({ key, direction });
    setCurrentSortOption({ key, direction });

    const sortedData = applySorting(getData, { key, direction });
    setGetData(sortedData);
  };

  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData({});
    setValidationErrors({});
    setAddData({
      weight_value: "",
      record_date: "",
      notes: "",
      unit: "kg",
    });
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;

    // Validate inputs
    let errors = { ...validationErrors };

    if (name === "weight_value") {
      // Check for more than one decimal place
      if (value.includes(".") && (value.split(".")[1] || "").length > 1) {
        errors[name] = "You can only enter up to one decimal place";
      }
      // Prevent negative values and allow only numbers with up to one decimal place
      else if (!/^\d*\.?\d*$/.test(value) || value.includes("-")) {
        errors[name] = "Please enter a valid positive number";
      } else {
        const numValue = parseFloat(value);
        const currentUnit = editData.id ? editData.unit : addData.unit;

        if (currentUnit === "kg") {
          if (numValue < 2) {
            errors[name] = "Weight must be at least 2 kg";
          } else if (numValue > 700) {
            errors[name] = "Weight must be less than 700 kg";
          } else {
            delete errors[name];
          }
        } else {
          if (numValue < 4) {
            errors[name] = "Weight must be at least 4 lbs";
          } else if (numValue > 1550) {
            errors[name] = "Weight must be less than 1550 lbs";
          } else {
            delete errors[name];
          }
        }
      }
    }

    setValidationErrors(errors);

    if (editData.id) {
      setEditData((prv) => ({ ...prv, [name]: value }));
    } else {
      setAddData((prv) => ({ ...prv, [name]: value }));
    }
  };

  const handleUnitChange = (e) => {
    const { value } = e.target;
    setAddData((prev) => ({ ...prev, unit: value }));

    // Clear validation errors when unit changes
    setValidationErrors({});
  };

  const validateForm = (formData) => {
    const errors = {};
    const weightValue = parseFloat(formData.weight_value);
    const currentUnit = formData.unit;

    if (!formData.weight_value || isNaN(weightValue)) {
      errors.weight_value = "Please enter a valid weight";
    } else if (currentUnit === "kg") {
      if (weightValue < 2) errors.weight_value = "Weight must be at least 2 kg";
      if (weightValue > 700)
        errors.weight_value = "Weight must be less than 700 kg";
    } else {
      if (weightValue < 4)
        errors.weight_value = "Weight must be at least 4 lbs";
      if (weightValue > 1550)
        errors.weight_value = "Weight must be less than 1550 lbs";
    }

    if (!formData.record_date) {
      errors.record_date = "Date is required";
    }

    // Check if date is in the future
    if (formData.record_date && new Date(formData.record_date) > new Date()) {
      errors.record_date = "Future dates are not allowed";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!validateForm(editData)) {
      return;
    }

    // Convert weight to kg for storage if needed
    let weightValueToStore = parseFloat(editData.weight_value);
    if (editData.unit === "lbs") {
      weightValueToStore = weightValueToStore / 2.20462; // Convert lbs to kg
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      record_id: editData?.id,
      form_data: {
        in_kg: weightValueToStore.toFixed(1),
        in_lbs: (weightValueToStore * 2.20462).toFixed(1),
        record_date: editData?.record_date,
        notes: editData?.notes,
      },
    };

    setLoadingEdit(true);
    try {
      const response = await SendPostRequest(
        "healthcare/weight/edit",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        handleModalClose();
        fetchData(currentFilter, currentSortOption);
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to update record");
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validateForm(addData)) {
      return;
    }

    // Convert weight to kg for storage if needed
    let weightValueToStore = parseFloat(addData.weight_value);
    if (addData.unit === "lbs") {
      weightValueToStore = weightValueToStore / 2.20462; // Convert lbs to kg
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      form_data: {
        in_kg: weightValueToStore.toFixed(1),
        in_lbs: (weightValueToStore * 2.20462).toFixed(1),
        record_date: addData.record_date,
        notes: addData.notes,
      },
    };

    setIsLoading(true);
    try {
      const response = await SendPostRequest(
        "healthcare/weight/add",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success("Record has been added successfully");
        handleModalClose();
        setAddData({
          weight_value: "",
          record_date: "",
          notes: "",
          unit: "kg",
        });
        fetchData(currentFilter, currentSortOption);
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to add record");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const data = {
      logged_id: patientData?.logged_id,
      record_id: deleteId,
    };

    setLoadingDelete(true);
    try {
      const response = await SendPostRequest("healthcare/weight/delete", data);
      if (response?.status === "success") {
        toast.success(response?.message);
        fetchData(currentFilter, currentSortOption);
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to delete record");
    } finally {
      setLoadingDelete(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleFilterChange = (e) => {
    const { value, name } = e.target;
    setFilterData((prv) => ({ ...prv, [name]: value }));
  };

  const handleFilter = async (e) => {
    e.preventDefault();
    if (!filterData?.date_from || !filterData?.date_to) {
      toast.error("Please select both from and to dates");
      return;
    }

    if (new Date(filterData.date_from) > new Date(filterData.date_to)) {
      toast.error("From date cannot be after to date");
      return;
    }

    setCurrentFilter(filterData);
    fetchData(filterData, currentSortOption);
  };

  const clearFilter = () => {
    setFilterData(null);
    setCurrentFilter(null);
    fetchData(null, currentSortOption);
  };

  const exportToCSV = () => {
    // Sort data by date descending for export
    const sortedData = [...getData].sort(
      (a, b) => new Date(b.record_date) - new Date(a.record_date)
    );

    const headers = [
      "SR.No",
      "Record Date",
      `Weight (${weightUnit})`,
      "Note",
      "Status",
    ];
    const csvData = sortedData.map((item, index) => [
      index + 1,
      formatDate(item.record_date),
      item.weight_value,
      item.notes || "--",
      item.status
        ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
        : "--",
    ]);

    // Add summary statistics
    if (sortedData.length > 0) {
      const weights = sortedData.map((item) => item.weight_value);
      const minWeight = Math.min(...weights);
      const maxWeight = Math.max(...weights);
      const avgWeight = (
        weights.reduce((a, b) => a + b, 0) / weights.length
      ).toFixed(1);

      csvData.push([]);
      csvData.push(["Summary Statistics"]);
      csvData.push(["Minimum Weight", `${minWeight} ${weightUnit}`]);
      csvData.push(["Maximum Weight", `${maxWeight} ${weightUnit}`]);
      csvData.push(["Average Weight", `${avgWeight} ${weightUnit}`]);
      csvData.push([]);
      csvData.push(["Report Generated On", formatDate(new Date())]);
    }

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `weight_management_${today}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    // Import jsPDF dynamically to avoid SSR issues
    import("jspdf")
      .then((jsPDFModule) => {
        import("jspdf-autotable").then((autoTableModule) => {
          const { jsPDF } = jsPDFModule;
          const { default: autoTable } = autoTableModule;

          const doc = new jsPDF();

          // Title
          doc.setFontSize(18);
          doc.text("Weight Management Records", 14, 22);
          doc.setFontSize(11);
          doc.setTextColor(100);
          doc.text(`Generated On: ${formatDate(new Date())}`, 14, 30);

          // Date range if filtered
          if (currentFilter?.date_from && currentFilter?.date_to) {
            doc.setFontSize(11);
            doc.text(
              `Date Range: ${formatDate(
                currentFilter.date_from
              )} to ${formatDate(currentFilter.date_to)}`,
              14,
              30
            );
          }

          // Sort data by date descending for PDF
          const sortedData = [...getData].sort(
            (a, b) => new Date(b.record_date) - new Date(a.record_date)
          );

          // Table data
          const tableData = sortedData.map((item, index) => [
            index + 1,
            formatDate(item.record_date),
            item.weight_value,
            item.notes || "-",
            item.status
              ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
              : "-",
          ]);

          // Table
          autoTable(doc, {
            startY: 35,
            head: [
              ["SR.No", "Date", `Weight (${weightUnit})`, "Notes", "Status"],
            ],
            body: tableData,
            theme: "grid",
            headStyles: {
              fillColor: [66, 139, 202],
            },
          });

          // Add summary statistics
          if (sortedData.length > 0) {
            const weights = sortedData.map((item) => item.weight_value);
            const minWeight = Math.min(...weights);
            const maxWeight = Math.max(...weights);
            const avgWeight = (
              weights.reduce((a, b) => a + b, 0) / weights.length
            ).toFixed(1);

            let finalY = doc.lastAutoTable.finalY + 10;

            doc.setFontSize(12);
            doc.text("Summary Statistics", 14, finalY);

            doc.setFontSize(10);
            doc.text(
              `Minimum Weight: ${minWeight} ${weightUnit}`,
              14,
              finalY + 7
            );
            doc.text(
              `Maximum Weight: ${maxWeight} ${weightUnit}`,
              14,
              finalY + 14
            );
            doc.text(
              `Average Weight: ${avgWeight} ${weightUnit}`,
              14,
              finalY + 21
            );

            // doc.text(`Report Generated On: ${formatDate(new Date())}`, 14, finalY + 35);
          }

          doc.save(`weight_records_${today}.pdf`);
        });
      })
      .catch((error) => {
        console.error("Error loading PDF libraries:", error);
        toast.error("Failed to generate PDF");
      });
  };

  const getWeightStatus = (weight) => {
    if (!userData.height) return "normal";

    // Calculate BMI (simplified)
    const heightInMeters = userData.height / 100;
    let bmi;

    if (weightUnit === "kg") {
      bmi = weight / (heightInMeters * heightInMeters);
    } else {
      // Convert lbs to kg for BMI calculation
      const weightInKg = weight * 0.453592;
      bmi = weightInKg / (heightInMeters * heightInMeters);
    }

    if (bmi < 18.5) return "underweight";
    if (bmi >= 18.5 && bmi <= 24.9) return "normal";
    if (bmi >= 25 && bmi <= 29.9) return "overweight";
    return "obese";
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "underweight":
        return "info";
      case "normal":
        return "success";
      case "overweight":
        return "warning";
      case "obese":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getCurrentSortOption = () => {
    if (!currentSortOption) return null;
    return sortOptions.find(
      (option) =>
        option.value.key === currentSortOption.key &&
        option.value.direction === currentSortOption.direction
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === "-" || e.key === "e" || e.key === "E") {
      e.preventDefault();
    }
  };

  return (
    <div className="weight-management-container Prescripition">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h3>Weight Management</h3>
        <div
          className="d-flex gap-2"
          style={{
            justifyContent: isMobile ? "space-between" : "",
            alignItems: isMobile ? "center" : "",
            width: isMobile ? "100%" : "",
          }}
        >
          <button
            className="btn btn-default d-flex align-items-center"
            onClick={handleModalOpen}
            style={{ alignItems: "center" }}
          >
            <MdAdd className="me-1" /> Add Record
          </button>
          <div>
            <button
              className="btn btn-default align-items-center"
              onClick={exportToCSV}
              style={{ alignItems: "center" }}
              disabled={getData.length === 0}
            >
              <MdFileDownload className="me-1" /> CSV
            </button>
            <button
              className="btn btn-default align-items-center"
              onClick={exportToPDF}
              style={{ alignItems: "center" }}
              disabled={getData.length === 0}
            >
              <MdFileDownload className="me-1" /> PDF
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`chart-views-btn ${
                viewMode === "chart"
                  ? "chart-active-views-btn"
                  : "chart-views-btn"
              }`}
              onClick={() => setViewMode("chart")}
            >
              Chart View
            </button>
            <button
              type="button"
              className={`chart-views-btn-second ${
                viewMode === "table"
                  ? "chart-active-views-btn"
                  : "chart-views-btn-second"
              }`}
              onClick={() => setViewMode("table")}
            >
              Table View
            </button>
          </div>

          <div className="d-flex align-items-center gap-3">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`chart-views-btn ${
                  weightUnit === "kg"
                    ? "chart-active-views-btn"
                    : "btn-outline-primary"
                }`}
                onClick={() => setWeightUnit("kg")}
              >
                kg
              </button>
              <button
                type="button"
                className={`chart-views-btn-second ${
                  weightUnit === "lbs"
                    ? "chart-active-views-btn"
                    : "chart-views-btn-second"
                }`}
                onClick={() => setWeightUnit("lbs")}
              >
                lbs
              </button>
            </div>

            {viewMode === "chart" && (
              <div className="d-flex align-items-center">
                <span className="me-2">Time Range:</span>
                <Select
                  className="time-range-select"
                  value={selectedTimeStamp}
                  options={timeStamp}
                  onChange={setSelectedTimeStamp}
                  isSearchable={false}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: "120px",
                    }),
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {viewMode === "chart" ? (
          <WeightManageChart
            data={graphData}
            loading={loadingGraph}
            unit={weightUnit}
            userData={userData}
          />
        ) : (
          <>
            {/* <div className="d-flex justify-content-between align-items-center mb-3"> */}
            <div className="d-flex align-items-center justify-content-between gap-2">
              <form
                onSubmit={handleFilter}
                className="d-flex gap-2 align-items-center justify-content-between flex-wrap"
              >
                <div>
                  <label htmlFor="from" className="form-label mb-0">
                    From:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_from"
                    className="form-control"
                    value={filterData?.date_from || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "140px" }}
                  />
                </div>
                <div>
                  <label htmlFor="to" className="form-label mb-0">
                    To:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_to"
                    className="form-control"
                    value={filterData?.date_to || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "140px" }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-default"
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  Filter
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={clearFilter}
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  Clear
                </button>

                <div
                  className="d-flex align-items-center ms-2"
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  <MdSort className="me-1" />
                  <Select
                    className="sort-select"
                    value={getCurrentSortOption()}
                    options={sortOptions}
                    onChange={handleSortChange}
                    isSearchable={false}
                    placeholder="Sort by..."
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minWidth: "180px",
                      }),
                    }}
                  />
                </div>
              </form>
            </div>
            {/* </div> */}

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table mt-2">
                  <thead className="thead-light">
                    <tr>
                      <th
                        scope="col"
                        style={{ width: "10%", textAlign: "center" }}
                      >
                        SR.No
                      </th>
                      <th scope="col">Record Date</th>
                      <th scope="col">Weight ({weightUnit})</th>
                      {/* <th scope="col">Note</th> */}
                      <th scope="col-2">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          No weight records found
                        </td>
                      </tr>
                    ) : (
                      getData.map((item, index) => {
                        const statusColor = item?.color;

                        return (
                          <tr key={item.id || index}>
                            <td style={{ textAlign: "center" }}>{index + 1}</td>
                            <td>{formatDate(item.record_date)}</td>
                            <td>{item.weight_value}</td>
                            {/* <td>{item.notes || "-"}</td> */}
                            <td className="col-2">
                              <span
                                className={`badge`}
                                style={{
                                  backgroundColor: statusColor,
                                  color: "#fff",
                                  textTransform: "capitalize",
                                  fontSize: "10px",
                                }}
                              >
                                {item.status || "--"}
                                {/* {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "Unknown"} */}
                              </span>
                            </td>
                            <td className="col-2">
                              <div className="action-buttons">
                                <button
                                  className="btn btn-default me-1"
                                  onClick={() => {
                                    setEditData({
                                      ...item,
                                      unit: weightUnit,
                                      weight_value: item.weight_value,
                                    });
                                    handleModalOpen();
                                  }}
                                  title="Edit"
                                >
                                  <MdEdit />
                                </button>
                                <button
                                  className="btn btn-default"
                                  onClick={() => {
                                    setDeleteId(item.id);
                                    setShowDeleteConfirm(true);
                                  }}
                                  disabled={LoadingDelete}
                                  title="Delete"
                                >
                                  {LoadingDelete && deleteId === item.id ? (
                                    <SpinnerBoot size="sm" />
                                  ) : (
                                    <MdDelete />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editData.id ? "Edit Weight Record" : "Add Weight Record"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={editData.id ? handleEdit : handleAdd}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Weight*</label>
                  <div className="input-group">
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${
                        validationErrors.weight_value ? "is-invalid" : ""
                      }`}
                      placeholder={`${
                        addData.unit === "kg" ? "2-700" : "4-1550"
                      }`}
                      value={
                        editData.id
                          ? editData.weight_value
                          : addData.weight_value
                      }
                      name="weight_value"
                      onChange={handleOnChange}
                      onKeyPress={handleKeyPress}
                      min={addData.unit === "kg" ? "2" : "4"}
                      max={addData.unit === "kg" ? "700" : "1550"}
                      required
                    />
                    <select
                      className="form-select weight-modal-select"
                      style={{ maxWidth: "80px" }}
                      value={editData.id ? editData.unit : addData.unit}
                      onChange={editData.id ? undefined : handleUnitChange}
                      // disabled={!!editData.id}
                      name="unit"
                    >
                      <option value="kg">kg</option>
                      <option value="lbs">lbs</option>
                    </select>
                  </div>
                  {validationErrors.weight_value && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.weight_value}
                    </div>
                  )}
                  <small className="form-text text-muted mt-0">
                    {addData.unit === "kg"
                      ? "Minimum: 2 kg, Maximum: 700 kg"
                      : "Minimum: 4 lbs, Maximum: 1550 lbs"}
                  </small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Date*</label>
                  <input
                    type="date"
                    className={`form-control weight-modal-select ${
                      validationErrors.record_date ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_date : addData.record_date
                    }
                    name="record_date"
                    onChange={handleOnChange}
                    max={today}
                    required
                  />
                  {validationErrors.record_date && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.record_date}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    placeholder="Add any notes about this measurement"
                    value={editData.id ? editData.notes : addData.notes}
                    name="notes"
                    onChange={handleOnChange}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              disableRipple
              className="btn btn-default"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              variant="btn btn-default"
              type="submit"
              disableRipple
              disabled={LoadingEdit || isLoading}
            >
              {LoadingEdit || isLoading ? "Saving..." : "Save Record"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this weight record? This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="btn btn-default"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={LoadingDelete}
          >
            {LoadingDelete ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WeightManagement;